import configuration from "@/configuration";
import instance from "../axiosConfig";
import { PKCEPair, SynchronisationStateResponse, TokenResponse } from "./models";

export class BimqSynchronisationService {
    private readonly POPUP_HEIGHT: number = 700;
    private readonly POPUP_WIDTH: number = 600;
    private readonly PKCE_CODE_VERIFIER_KEY: string = "bimq-pkce-verifier";
    private readonly BYTES: number = 22;
    private readonly ALGORITHM: string = "SHA-256";

    private regionCode: string;
    private authorizeEndpoint: string;
    private clientId: string;
    private redirectUri: string;

    private accessToken!: string | null;
    private onMessage!: (message: MessageEvent) => void;

    constructor() {
        this.regionCode = "int";
        this.authorizeEndpoint = configuration.bimq.authorizeEndpoint;
        this.clientId = configuration.bimq.clientId;
        this.redirectUri = configuration.bimq.redirectUri;
    }

    private generatePKCEPair = async (): Promise<PKCEPair> => {
        const random = new Uint8Array(this.BYTES);
        crypto.getRandomValues(random);

        const verifier = Array.from(random)
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");

        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const hashBuffer = await crypto.subtle.digest(this.ALGORITHM, data);

        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const base64 = btoa(String.fromCharCode(...hashArray));
        const challenge = base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

        return { challenge, verifier };
    };

    private registerListener = () => window.addEventListener("message", this.onMessage);
    private removeListener = () => window.removeEventListener("message", this.onMessage);

    authorize = async (onMessage: (message: MessageEvent) => void): Promise<Window | null> => {
        this.onMessage = onMessage;
        this.registerListener();

        const top = window.outerHeight / 2 + window.screenY - this.POPUP_HEIGHT / 2;
        const left = window.outerWidth / 2 + window.screenX - this.POPUP_WIDTH / 2;
        const pkce = await this.generatePKCEPair();
        sessionStorage.setItem(this.PKCE_CODE_VERIFIER_KEY, pkce.verifier);

        const url = `${this.authorizeEndpoint}?client_id=${this.clientId}&code_challenge=${pkce.challenge}&code_challenge_method=S256&redirect_uri=${this.redirectUri}&response_type=code`;
        const popup = window.open(
            url,
            "BIMQ OAuth2 Popup",
            `height=${this.POPUP_HEIGHT},width=${this.POPUP_WIDTH},top=${top},left=${left}`
        );

        return popup;
    };

    authenticate = async (code: string): Promise<boolean> => {
        const verifier = sessionStorage.getItem(this.PKCE_CODE_VERIFIER_KEY);
        const response = await instance.postForm<TokenResponse>(
            `BIMQ/Token`,
            {
                code: code,
                code_verifier: verifier,
                redirect_uri: this.redirectUri,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );

        this.accessToken = response.data.access_token;

        return Boolean(response.data.access_token);
    };

    synchronize = async (
        ignoreChanges: boolean,
        ignoreFootnotes: boolean,
        ignoreVisibility: boolean,
        attachment: File | undefined
    ): Promise<boolean> => {
        const formData = new FormData();
        formData.append("keepoverride", ignoreChanges.toString());
        formData.append("keepFootnotes", ignoreFootnotes.toString());
        formData.append("keepVisibility", ignoreVisibility.toString());
        formData.append("bimqToken", this.accessToken!);
        if (attachment) formData.append("PropertyOverrideExcel", attachment);

        const response = await instance.post<FormData>(`api/admin/${this.regionCode}/synchronize`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        return response.status === 204 || response.status === 200;
    };

    getState = async (): Promise<SynchronisationStateResponse> => {
        const response = await instance.get<SynchronisationStateResponse>(
            `api/admin/${this.regionCode}/synchronizationstate`,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    clean = () => {
        this.removeListener();
    };
}
